import {
  SingleNamedObject,
  SvgRectangleArea,
  getItemByField,
  FixedDimension,
  PercentDimension,
  ColorConverter,
  roundNumber
} from "../../web-graphics";
import { Plot } from "../core/objects/svg/plot";
import { Cursor } from "./cursor";
import { Measurer } from "./measurer";

export class OfflineDataPlotObjectGroup {
  constructor(plotObject) {
    this.plotObject = plotObject;

    this.plotObject.itemsSettings.cursor = Plot.findContentSettings(this.plotObject.settings, "cursor", "aboveCentral");
    this.plotObject.itemsSettings.measurer = Plot.findContentSettings(this.plotObject.settings, "measurer", "aboveCentral");
  }

  startUpdateMeanValues() {
    setInterval(this.plotObject.tryUpdateMeanSignalsValue.bind(this.plotObject), 1000);
  }

  initUpdateMeanValueFunctions(signalObject) {
    if (signalObject.setIsolineChangeAction !== undefined) {
      signalObject.setIsolineChangeAction(function () {});
    }
  }

  initCursorAndMeasurer() {
    this.aboveSignalsSvgArea = new SvgRectangleArea(
      {
        location: {
          x: new FixedDimension(0),
          y: new FixedDimension(0)
        },
        size: {
          width: new PercentDimension(1),
          height: new PercentDimension(1)
        },
        parent: {
          ref: this.plotObject.signalsBox.content.aboveCentral
        },
        htmlObject: {
          parent: {
            ref: this.plotObject.settings.htmlObject.ref
          },
          graphicParent: this.plotObject
        }
      }
    );

    this.measurerMode = "cursor"; // cursor, measurer
    this.isCursorInitialized = false;
    this.isMeasurerInitialized = false;

    const cursorParsedColor = ColorConverter.convertWebglRgbaToHex(this.plotObject.itemsSettings.cursor.color);
    let cursorColor = "#000000";
    if (cursorParsedColor.success) {
      cursorColor = cursorParsedColor.value;
    }
    const scaleX = this.plotObject.settings.signalsBox.scaleX.currValue / this.plotObject.settings.signalsBox.defaultCellSize_mm;
    const widthInMs = ((this.plotObject.getSignalsWidthInMm() / this.plotObject.settings.signalsBox.defaultCellSize_mm) * 1000) / scaleX;
    this.cursorStartPos =
      (this.plotObject.itemsSettings.cursor.startPosition / widthInMs) * this.plotObject.signalsBox.content.central.settings.sizeInPixels.width;

    this.cursor = new Cursor(
      {
        location: {
          x: new FixedDimension(0),
          y: new FixedDimension(0)
        },
        size: {
          width: new PercentDimension(1),
          height: new PercentDimension(1)
        },
        parent: {
          ref: this.aboveSignalsSvgArea
        },
        htmlObject: {
          parent: {
            ref: this.aboveSignalsSvgArea.settings.htmlObject.ref
          },
          graphicParent: this.aboveSignalsSvgArea
        },
        touchLineAreaWidth: this.plotObject.itemsSettings.cursor.touchLineAreaWidth,
        startLinePos: this.cursorStartPos,
        color: cursorColor,
        maxX: 0
      }
    );
    this.aboveSignalsSvgArea.childItemsContainer.add(new SingleNamedObject("Cursor", this.cursor));

    if (this.plotObject.itemsSettings.measurer !== undefined) {
      this.measurerStartPos =
        (this.plotObject.itemsSettings.measurer.startPosition / widthInMs) * this.plotObject.signalsBox.content.central.settings.sizeInPixels.width;
      this.measurerStartWidth =
        (this.plotObject.itemsSettings.measurer.startWidth / widthInMs) * this.plotObject.signalsBox.content.central.settings.sizeInPixels.width;

      const leftLineMeasurerParsedColor = ColorConverter.convertWebglRgbaToHex(this.plotObject.itemsSettings.measurer.leftLineColor);
      let leftLineMeasurerColor = "#000000";
      if (leftLineMeasurerParsedColor.success) {
        leftLineMeasurerColor = leftLineMeasurerParsedColor.value;
      }
      const rightLineMeasurerParsedColor = ColorConverter.convertWebglRgbaToHex(this.plotObject.itemsSettings.measurer.rightLineColor);
      let rightLineMeasurerColor = "#000000";
      if (rightLineMeasurerParsedColor.success) {
        rightLineMeasurerColor = rightLineMeasurerParsedColor.value;
      }
      const focusedAreaParsedColor = ColorConverter.convertWebglRgbaToHex(this.plotObject.itemsSettings.measurer.focusedAreaColor);
      let focusedAreaColor = "#000000";
      if (focusedAreaParsedColor.success) {
        focusedAreaColor = focusedAreaParsedColor.value;
      }
      const commonLabelParsedColor = ColorConverter.convertWebglRgbaToHex(this.plotObject.itemsSettings.measurer.commonLabelColor);
      let commonLabelColor = "#000000";
      if (focusedAreaParsedColor.success) {
        commonLabelColor = commonLabelParsedColor.value;
      }
      const intermediateAreaParsedColor = ColorConverter.convertWebglRgbaToHex(
        this.plotObject.itemsSettings.measurer.intermediateAreaColor
      );
      let intermediateAreaColor = "#00000000";
      if (intermediateAreaParsedColor.success) {
        intermediateAreaColor = intermediateAreaParsedColor.value;
      }

      this.measurer = new Measurer(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(0)
          },
          size: {
            width: new PercentDimension(1),
            height: new PercentDimension(1)
          },
          parent: {
            ref: this.aboveSignalsSvgArea
          },
          htmlObject: {
            parent: {
              ref: this.aboveSignalsSvgArea.settings.htmlObject.ref
            },
            graphicParent: this.aboveSignalsSvgArea
          },
          startLeftLinePos: this.measurerStartPos,
          startRightLinePos: this.measurerStartPos + this.measurerStartWidth,
          touchLineAreaWidth: this.plotObject.itemsSettings.measurer.touchLineAreaWidth,
          minLinesSpace: this.plotObject.itemsSettings.measurer.minLinesSpace,
          leftLineColor: leftLineMeasurerColor,
          rightLineColor: rightLineMeasurerColor,
          focusedAreaColor: focusedAreaColor,
          commonLabelColor: commonLabelColor,
          intermediateAreaColor: intermediateAreaColor,
          maxX: 0
        }
      );
      this.measurer.setVisibility(false);
      this.aboveSignalsSvgArea.childItemsContainer.add(new SingleNamedObject("Measurer", this.measurer));

      this.measurerLeftBufferIndices = new Map();
      this.measurerRightBufferIndices = new Map();
      this.initBufferIndicesMap(this.measurerLeftBufferIndices);
      this.initBufferIndicesMap(this.measurerRightBufferIndices);

      this.measurer.onPositionChanged = this.onMeasurerPosChanged.bind(this);
    }

    this.plotObject.signalsBox.content.aboveCentral.addItem(new SingleNamedObject("AboveSignalsSvgArea", this.aboveSignalsSvgArea));

    this.cursorBufferIndices = new Map();
    this.initBufferIndicesMap(this.cursorBufferIndices);
    this.cursor.onPositionChanged = this.onCursorPosChanged.bind(this);
  }

  initBufferIndicesMap(bufferIndicesMap) {
    this.plotObject.settings.dataSources.forEach(dataSourceSettings => {
      const channelsMap = new Map();
      bufferIndicesMap.set(dataSourceSettings.id, channelsMap);
      dataSourceSettings.channels.forEach(channelSettings => {
        const configurationsMap = new Map();
        channelsMap.set(channelSettings.id, configurationsMap);
        channelSettings.configurations.forEach(configurationSettings => {
          const signalsMap = new Map();
          configurationsMap.set(configurationSettings.name, signalsMap);
          configurationSettings.signals.forEach(signalSettings => {
            signalsMap.set(signalSettings.id, {
              index: 0,
              x: 0,
              maxX: 0,
              maxIndex: 0
            });
          });
        });
      });
    });
  }

  setCursorOrMeasurerPosition(x) {
    if (this.measurerMode == "cursor") {
      this.cursor.move(x);
    } else if (this.measurerMode == "measurer") {
      this.measurer.moveLinesByLeft(x);
    }
    this.updateCursorAndMeasurerPosition(false);
  }

  setOnCursorPositionChanged(action) {
    this.cursor.onPositionChanged = () => {
      this.onCursorPosChanged();
      action(this.cursor.linePos);
    };
  }

  setOnMeasurerPositionChanged(action) {
    this.measurer.onPositionChanged = () => {
      this.onMeasurerPosChanged();
      action(this.measurer.leftLineX, this.measurer.rightLineX);
    };
  }

  updateBufferIndicesMap(x, bufferIndicesMap) {
    let newMaxX = undefined;
    const selectedDataSourceId = this.plotObject.selectedChannel.id_dataSource;
    const selectedChannelId = this.plotObject.selectedChannel.id_channel;
    this._forAllChannels(bufferIndicesMap, (channelSettings, signalSettings, signalsMap) => {
      const signalObject = this.plotObject.getSignalObject(channelSettings.id_dataSource, channelSettings.id, signalSettings.id);
      const maxSignalIndex = signalObject.getLastValidGraphicDataIndex();
      if (maxSignalIndex === undefined) {
        return;
      }
      const maxSignalX = signalObject.getLastValidPointX();
      if (
        channelSettings.id_dataSource == selectedDataSourceId &&
        channelSettings.id == selectedChannelId &&
        (newMaxX === undefined || newMaxX > maxSignalX)
      ) {
        newMaxX = maxSignalX;
      }
      let signalX = x;
      let signalIndex = signalObject.getFirstBufferIndex(x);
      if (signalIndex === undefined) {
        signalIndex = maxSignalIndex;
        signalX = maxSignalX;
      }
      signalsMap.set(signalSettings.id, {
        x: signalX,
        index: signalIndex,
        maxX: maxSignalX,
        maxIndex: maxSignalIndex
      });
    });
    return newMaxX;
  }

  updateCursorBufferIndicesMap(x) {
    let newMaxX = this.updateBufferIndicesMap(x, this.cursorBufferIndices);
    if (newMaxX !== undefined) {
      this.cursor.settings.maxX = newMaxX;
    }
  }

  updateLeftLineMeasurerBufferIndicesMap(x) {
    let newMaxX = this.updateBufferIndicesMap(x, this.measurerLeftBufferIndices);
    if (newMaxX !== undefined) {
      this.measurer.settings.maxX = newMaxX;
    }
  }

  updateRightLineMeasurerBufferIndicesMap(x) {
    let newMaxX = this.updateBufferIndicesMap(x, this.measurerRightBufferIndices);
    if (newMaxX !== undefined) {
      this.measurer.settings.maxX = newMaxX;
    }
  }

  updateMeanValues() {
    this.plotObject.tryUpdateMeanSignalsValue();
    if (this.measurerMode == "cursor") {
      this._updateMeanValuesByCursor();
    } else if (this.measurerMode == "measurer") {
      this._updateMeanValuesByMeasurer();
    }
  }

  onCursorPosChanged() {
    if (!this.cursor.getVisibility()) {
      return;
    }
    this.updateCursorBufferIndicesMap(this.cursor.linePos);
    this._updateMeanValuesByCursor();
  }

  onMeasurerPosChanged() {
    if (!this.measurer.getVisibility()) {
      return;
    }
    this.updateLeftLineMeasurerBufferIndicesMap(this.measurer.leftLineX);
    this.updateRightLineMeasurerBufferIndicesMap(this.measurer.rightLineX);
    this._updateMeanValuesByMeasurer();
  }

  setOnMeasurerSpaceBetweenLinesChanged(action) {
    this.measurer.onSpaceBetweenLinesChanged = action;
  }

  changeMeasurerMode() {
    if (this.measurerMode == "cursor") {
      this.measurerMode = "measurer";
      this._setMeasurerModeToMeasurer();
    } else {
      this.measurerMode = "cursor";
      this._setMeasurerModeToCursor();
    }
  }

  updateMeasurerMode() {
    if (this.measurerMode == "cursor") {
      this._setMeasurerModeToCursor();
    } else {
      this._setMeasurerModeToMeasurer();
    }
  }

  convertMsToPixels(ms) {
    const scaleX = this.plotObject.settings.signalsBox.scaleX.currValue / this.plotObject.settings.signalsBox.defaultCellSize_mm;
    return scaleX * (ms / this.getSignalWidthInMs()) * this.getSignalWidthInPixels();
  }

  convertPixelsToMs(pixels) {
    const scaleX = this.plotObject.settings.signalsBox.scaleX.currValue / this.plotObject.settings.signalsBox.defaultCellSize_mm;
    return ((pixels / this.getSignalWidthInPixels()) * this.getSignalWidthInMs()) / scaleX;
  }

  getSignalWidthInPixels() {
    return this.plotObject.signalsBox.content.central.settings.sizeInPixels.width;
  }

  getSignalWidthInMs() {
    const widthInMm = this.plotObject.getSignalsWidthInMm();
    return (widthInMm / this.plotObject.settings.signalsBox.defaultCellSize_mm) * 1000; // 1 cell = 1 sec
  }

  getSpaceBetweenLines() {
    return this.measurer.getSpaceBetweenLines();
  }

  setSpaceBetweenLines(widthInPixels) {
    this.measurer.setSpaceBetweenLines(widthInPixels);
  }

  changeMeasurerMoveMode(mode) {
    this.measurer.changeMoveMode(mode);
  }

  getKeybordEventHanlder() {
    return event => {
      if (this.measurerMode == "cursor") {
        this._handleCursorKeyboardEvent(event);
      } else {
        this._handleMeasurerKeyboardEvent(event);
      }
    };
  }

  _syncCursorPositionToMeasurer() {
    if (this.measurer === undefined) {
      return;
    }

    let newMaxX = undefined;
    const selectedDataSourceId = this.plotObject.selectedChannel.id_dataSource;
    const selectedChannelId = this.plotObject.selectedChannel.id_channel;
    const dataSources = this.plotObject.settings.dataSources;
    for (let sourceIndex = 0; sourceIndex < dataSources.length; sourceIndex++) {
      const dataSourceSettings = dataSources[sourceIndex];
      const cursorChannelsMap = this.cursorBufferIndices.get(dataSourceSettings.id);
      const leftLineChannelsMap = this.measurerLeftBufferIndices.get(dataSourceSettings.id);

      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const channelSettings = dataSourceSettings.channels[channelIndex];
        const cursorConfigurationsMap = cursorChannelsMap.get(channelSettings.id);
        const leftLineConfigurationsMap = leftLineChannelsMap.get(channelSettings.id);

        const currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
        const cursorSignalsMap = cursorConfigurationsMap.get(channelSettings.currConfiguration);
        const leftLineSignalsMap = leftLineConfigurationsMap.get(channelSettings.currConfiguration);
        for (let signalIndex = 0; signalIndex < currConfiguration.signals.length; signalIndex++) {
          const signalId = currConfiguration.signals[signalIndex].id;
          const signalObject = this.plotObject.getSignalObject(dataSourceSettings.id, channelSettings.id, signalId);
          const leftLineInfo = leftLineSignalsMap.get(signalId);
          const cursorLineInfo = cursorSignalsMap.get(signalId);
          if (cursorLineInfo.x < 0) {
            this.isNeedSyncCursorPos = true;
            return;
          }
          const maxIndex = signalObject.getLastValidGraphicDataIndex();
          const maxX = signalObject.getLastValidPointX();
          if (maxIndex === undefined || maxX === undefined) {
            this.isNeedSyncCursorPos = true;
            return;
          }

          let newSignalIndex = leftLineInfo.index;
          let newX = leftLineInfo.x;
          if (newSignalIndex > maxIndex || newX > maxX) {
            newSignalIndex = maxIndex;
            newX = maxX;
          }
          cursorSignalsMap.set(signalId, {
            x: newX,
            index: newSignalIndex,
            maxX: maxX,
            maxIndex: maxIndex
          });

          if (dataSourceSettings.id == selectedDataSourceId && channelSettings.id == selectedChannelId) {
            if (this.cursor.settings.maxX === undefined || this.cursor.settings.maxX < maxX) {
              this.cursor.settings.maxX = maxX;
            }
            if (newMaxX === undefined || newMaxX > maxX) {
              newMaxX = maxX;
            }
            if (cursorLineInfo.x != newX) {
              const onPositionChangedAction = this.cursor.onPositionChanged;
              this.cursor.onPositionChanged = undefined;
              this.cursor.move(newX);
              this.cursor.onPositionChanged = onPositionChangedAction;
            }
          }
        }
      }
    }
    if (newMaxX !== undefined) {
      this.cursor.settings.maxX = newMaxX;
      this.measurer.settings.maxX = newMaxX;
    }
  }

  _syncMeasurerPositionToCursor() {
    let newMaxX = undefined;
    const selectedDataSourceId = this.plotObject.selectedChannel.id_dataSource;
    const selectedChannelId = this.plotObject.selectedChannel.id_channel;
    const dataSources = this.plotObject.settings.dataSources;
    for (let sourceIndex = 0; sourceIndex < dataSources.length; sourceIndex++) {
      const dataSourceSettings = dataSources[sourceIndex];
      const cursorChannelsMap = this.cursorBufferIndices.get(dataSourceSettings.id);
      const leftLineChannelsMap = this.measurerLeftBufferIndices.get(dataSourceSettings.id);
      const rightLineChannelsMap = this.measurerRightBufferIndices.get(dataSourceSettings.id);

      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const channelSettings = dataSourceSettings.channels[channelIndex];
        const cursorConfigurationsMap = cursorChannelsMap.get(channelSettings.id);
        const leftLineConfigurationsMap = leftLineChannelsMap.get(channelSettings.id);
        const rightLineConfigurationsMap = rightLineChannelsMap.get(channelSettings.id);

        const currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
        const cursorSignalsMap = cursorConfigurationsMap.get(channelSettings.currConfiguration);
        const leftLineSignalsMap = leftLineConfigurationsMap.get(channelSettings.currConfiguration);
        const rightLineSignalsMap = rightLineConfigurationsMap.get(channelSettings.currConfiguration);

        for (let signalIndex = 0; signalIndex < currConfiguration.signals.length; signalIndex++) {
          const signalId = currConfiguration.signals[signalIndex].id;
          const signalObject = this.plotObject.getSignalObject(dataSourceSettings.id, channelSettings.id, signalId);
          const cursorLineInfo = cursorSignalsMap.get(signalId);
          if (cursorLineInfo.x < 0) {
            this.isNeedSyncMeasurePos = true;
            return;
          }
          const maxIndex = signalObject.getLastValidGraphicDataIndex();
          const maxX = signalObject.getLastValidPointX();
          if (maxIndex === undefined || maxX === undefined) {
            this.isNeedSyncMeasurePos = true;
            return;
          }
          if (this.measurer.settings.maxX === undefined || this.measurer.settings.maxX < maxX) {
            this.measurer.settings.maxX = maxX;
          }
          if (
            dataSourceSettings.id == selectedDataSourceId &&
            channelSettings.id == selectedChannelId &&
            (newMaxX === undefined || newMaxX > maxX)
          ) {
            newMaxX = maxX;
          }
          let newLeftLineIndex = cursorLineInfo.index;
          let newLeftLineX = cursorLineInfo.x;
          let newRightLineX = newLeftLineX + this.measurer.spaceBetweenLines;
          if (newLeftLineIndex > maxIndex || newRightLineX > maxX) {
            newRightLineX = maxX;
            newLeftLineX = maxX - this.measurer.spaceBetweenLines;
            if (newLeftLineX < 0) {
              newLeftLineX = 0;
              newLeftLineIndex = signalObject.getFirstBufferIndex(0);
            }
            newLeftLineIndex = signalObject.getFirstBufferIndex(newLeftLineX);
          }
          let newRightLineIndex = signalObject.getFirstBufferIndex(newRightLineX);

          leftLineSignalsMap.set(signalId, {
            x: newLeftLineX,
            index: newLeftLineIndex,
            maxX: maxX,
            maxIndex: maxIndex
          });
          rightLineSignalsMap.set(signalId, {
            x: newRightLineX,
            index: newRightLineIndex,
            maxX: maxX,
            maxIndex: maxIndex
          });
          if (dataSourceSettings.id == selectedDataSourceId && channelSettings.id == selectedChannelId) {
            const onPositionChangedAction = this.measurer.onPositionChanged;
            this.measurer.onPositionChanged = undefined;
            this.measurer.moveLines(newLeftLineX, newRightLineX);
            this.measurer.onPositionChanged = onPositionChangedAction;
          }
        }
      }
    }

    if (newMaxX !== undefined) {
      this.cursor.settings.maxX = newMaxX;
      this.measurer.settings.maxX = newMaxX;
    }
  }

  _handleCursorKeyboardEvent(event) {
    let isNeedUpdate = false;

    const selectedDataSourceId = this.plotObject.selectedChannel.id_dataSource;
    const selectedChannelId = this.plotObject.selectedChannel.id_channel;
    const dataSources = this.plotObject.settings.dataSources;
    for (let sourceIndex = 0; sourceIndex < dataSources.length; sourceIndex++) {
      const dataSourceSettings = dataSources[sourceIndex];
      const channelsMap = this.cursorBufferIndices.get(dataSourceSettings.id);
      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const channelSettings = dataSourceSettings.channels[channelIndex];
        const configurationsMap = channelsMap.get(channelSettings.id);
        const currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
        const signalsMap = configurationsMap.get(channelSettings.currConfiguration);

        for (let signalIndex = 0; signalIndex < currConfiguration.signals.length; signalIndex++) {
          const signalId = currConfiguration.signals[signalIndex].id;
          const signalObject = this.plotObject.getSignalObject(dataSourceSettings.id, channelSettings.id, signalId);
          const cursorLineInfo = signalsMap.get(signalId);
          const maxIndex = signalObject.getLastValidGraphicDataIndex();
          const maxX = signalObject.getLastValidPointX();
          switch (event.key) {
            case "ArrowLeft":
              {
                if (cursorLineInfo.index - 1 >= 0) {
                  const oldX = cursorLineInfo.x;
                  const newX = signalObject.getPointX(cursorLineInfo.index - 1);
                  signalsMap.set(signalId, {
                    x: newX,
                    index: cursorLineInfo.index - 1,
                    maxX: maxX,
                    maxIndex: maxIndex
                  });
                  if (dataSourceSettings.id == selectedDataSourceId && channelSettings.id == selectedChannelId && oldX != newX) {
                    const onPositionChangedAction = this.cursor.onPositionChanged;
                    this.cursor.onPositionChanged = undefined;
                    this.cursor.move(newX);
                    this.cursor.onPositionChanged = onPositionChangedAction;
                  }
                  isNeedUpdate = true;
                }
              }
              break;
            case "ArrowRight":
              {
                if (cursorLineInfo.index + 1 <= maxIndex) {
                  const oldX = cursorLineInfo.x;
                  const newX = signalObject.getPointX(cursorLineInfo.index + 1);
                  signalsMap.set(signalId, {
                    x: newX,
                    index: cursorLineInfo.index + 1,
                    maxX: maxX,
                    maxIndex: maxIndex
                  });
                  if (dataSourceSettings.id == selectedDataSourceId && channelSettings.id == selectedChannelId && oldX != newX) {
                    const onPositionChangedAction = this.cursor.onPositionChanged;
                    this.cursor.onPositionChanged = undefined;
                    this.cursor.move(newX);
                    this.cursor.onPositionChanged = onPositionChangedAction;
                  }
                  isNeedUpdate = true;
                }
              }
              break;
          }
        }
      }
    }

    if (isNeedUpdate) {
      this._updateMeanValuesByCursor();
    }
  }

  _handleMeasurerKeyboardEvent(event) {
    let isNeedUpdate = false;
    const selectedDataSourceId = this.plotObject.selectedChannel.id_dataSource;
    const selectedChannelId = this.plotObject.selectedChannel.id_channel;
    const dataSources = this.plotObject.settings.dataSources;
    for (let sourceIndex = 0; sourceIndex < dataSources.length; sourceIndex++) {
      const dataSourceSettings = dataSources[sourceIndex];
      const leftLineChannelsMap = this.measurerLeftBufferIndices.get(dataSourceSettings.id);
      const rightLineChannelsMap = this.measurerRightBufferIndices.get(dataSourceSettings.id);
      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const channelSettings = dataSourceSettings.channels[channelIndex];
        const leftLineConfigurationsMap = leftLineChannelsMap.get(channelSettings.id);
        const rightLineConfigurationsMap = rightLineChannelsMap.get(channelSettings.id);
        const currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
        const leftLineSignalsMap = leftLineConfigurationsMap.get(channelSettings.currConfiguration);
        const rightLineSignalsMap = rightLineConfigurationsMap.get(channelSettings.currConfiguration);
        for (let signalIndex = 0; signalIndex < currConfiguration.signals.length; signalIndex++) {
          const signalId = currConfiguration.signals[signalIndex].id;
          const signalObject = this.plotObject.getSignalObject(dataSourceSettings.id, channelSettings.id, signalId);
          const maxIndex = signalObject.getLastValidGraphicDataIndex();
          const maxX = signalObject.getLastValidPointX();
          if (maxIndex === undefined || maxX === undefined) {
            continue;
          }
          const leftLineInfo = leftLineSignalsMap.get(signalId);
          const rightLineInfo = rightLineSignalsMap.get(signalId);

          switch (event.key) {
            case "ArrowLeft":
              {
                if (this.measurer.isLeftLineFocused) {
                  if (leftLineInfo.index - 1 >= 0) {
                    const newLeftLineX = signalObject.getPointX(leftLineInfo.index - 1);
                    leftLineSignalsMap.set(signalId, {
                      x: newLeftLineX,
                      index: leftLineInfo.index - 1,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    if (
                      dataSourceSettings.id == selectedDataSourceId &&
                      channelSettings.id == selectedChannelId &&
                      this.measurer.leftLineX != newLeftLineX
                    ) {
                      const onPositionChangedAction = this.measurer.onPositionChanged;
                      this.measurer.onPositionChanged = undefined;
                      this.measurer.moveLine(newLeftLineX, true);
                      this.measurer.onPositionChanged = onPositionChangedAction;
                    }
                    isNeedUpdate = true;
                  }
                } else if (this.measurer.isRightLineFocused) {
                  const minLeftBorderX = leftLineInfo.x + this.measurer.settings.minLinesSpace;
                  let minLeftBorderIndex =
                    leftLineInfo.x == minLeftBorderX ? leftLineInfo.index : signalObject.getFirstBufferIndex(minLeftBorderX);
                  if (rightLineInfo.index - 1 >= minLeftBorderIndex) {
                    const newRightLineX = signalObject.getPointX(rightLineInfo.index - 1);
                    rightLineSignalsMap.set(signalId, {
                      x: newRightLineX,
                      index: rightLineInfo.index - 1,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    if (
                      dataSourceSettings.id == selectedDataSourceId &&
                      channelSettings.id == selectedChannelId &&
                      this.measurer.rightLineX != newRightLineX
                    ) {
                      const onPositionChangedAction = this.measurer.onPositionChanged;
                      this.measurer.onPositionChanged = undefined;
                      this.measurer.moveLine(newRightLineX, false);
                      this.measurer.onPositionChanged = onPositionChangedAction;
                    }
                    isNeedUpdate = true;
                  }
                } else {
                  if (leftLineInfo.index - 1 >= 0) {
                    const newLeftLineX = signalObject.getPointX(leftLineInfo.index - 1);
                    let newRightLineIndex = rightLineInfo.index - 1;
                    let newRightLineX = signalObject.getPointX(newRightLineIndex);
                    if (newRightLineX - newLeftLineX < this.measurer.settings.minLinesSpace) {
                      newRightLineX = newLeftLineX + this.measurer.settings.minLinesSpace;
                      newRightLineIndex = signalObject.getFirstBufferIndex(newRightLineX);
                    }
                    leftLineSignalsMap.set(signalId, {
                      x: newLeftLineX,
                      index: leftLineInfo.index - 1,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    rightLineSignalsMap.set(signalId, {
                      x: newRightLineX,
                      index: newRightLineIndex,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    if (dataSourceSettings.id == selectedDataSourceId && channelSettings.id == selectedChannelId) {
                      if (this.measurer.leftLineX != newLeftLineX) {
                        const onPositionChangedAction = this.measurer.onPositionChanged;
                        this.measurer.onPositionChanged = undefined;
                        this.measurer.moveLine(newLeftLineX, true);
                        this.measurer.onPositionChanged = onPositionChangedAction;
                      }
                      if (this.measurer.rightLineX != newRightLineX) {
                        const onPositionChangedAction = this.measurer.onPositionChanged;
                        this.measurer.onPositionChanged = undefined;
                        this.measurer.moveLine(newRightLineX, false);
                        this.measurer.onPositionChanged = onPositionChangedAction;
                      }
                    }
                    isNeedUpdate = true;
                  }
                }
              }
              break;
            case "ArrowRight":
              {
                if (this.measurer.isLeftLineFocused) {
                  const maxRightBorderX = rightLineInfo.x - this.measurer.settings.minLinesSpace;
                  let maxRightBorderIndex =
                    rightLineInfo.x == maxRightBorderX ? rightLineInfo.index : signalObject.getLastBufferIndex(maxRightBorderX);
                  if (leftLineInfo.index + 1 <= maxRightBorderIndex) {
                    const newLeftLineX = signalObject.getPointX(leftLineInfo.index + 1);
                    leftLineSignalsMap.set(signalId, {
                      x: newLeftLineX,
                      index: leftLineInfo.index + 1,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    if (
                      dataSourceSettings.id == selectedDataSourceId &&
                      channelSettings.id == selectedChannelId &&
                      this.measurer.leftLineX != newLeftLineX
                    ) {
                      const onPositionChangedAction = this.measurer.onPositionChanged;
                      this.measurer.onPositionChanged = undefined;
                      this.measurer.moveLine(newLeftLineX, true);
                      this.measurer.onPositionChanged = onPositionChangedAction;
                    }
                    isNeedUpdate = true;
                  }
                } else if (this.measurer.isRightLineFocused) {
                  if (rightLineInfo.index + 1 <= maxIndex) {
                    const newRightLineX = signalObject.getPointX(rightLineInfo.index + 1);
                    rightLineSignalsMap.set(signalId, {
                      x: newRightLineX,
                      index: rightLineInfo.index + 1,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    if (
                      dataSourceSettings.id == selectedDataSourceId &&
                      channelSettings.id == selectedChannelId &&
                      this.measurer.rightLineX != newRightLineX
                    ) {
                      const onPositionChangedAction = this.measurer.onPositionChanged;
                      this.measurer.onPositionChanged = undefined;
                      this.measurer.moveLine(newRightLineX, false);
                      this.measurer.onPositionChanged = onPositionChangedAction;
                    }
                    isNeedUpdate = true;
                  }
                } else {
                  if (rightLineInfo.index + 1 <= maxIndex) {
                    const newRightLineIndex = rightLineInfo.index + 1;
                    const newRightLineX = signalObject.getPointX(newRightLineIndex);
                    let newLeftLineIndex = leftLineInfo.index + 1;
                    let newLeftLineX = signalObject.getPointX(newLeftLineIndex);
                    if (newRightLineX - newLeftLineX < this.measurer.settings.minLinesSpace) {
                      newLeftLineX = newRightLineX - this.measurer.settings.minLinesSpace;
                      newLeftLineIndex = signalObject.getLastBufferIndex(newLeftLineX);
                    }
                    leftLineSignalsMap.set(signalId, {
                      x: newLeftLineX,
                      index: newLeftLineIndex,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    rightLineSignalsMap.set(signalId, {
                      x: newRightLineX,
                      index: newRightLineIndex,
                      maxX: maxX,
                      maxIndex: maxIndex
                    });
                    if (dataSourceSettings.id == selectedDataSourceId && channelSettings.id == selectedChannelId) {
                      if (this.measurer.leftLineX != newLeftLineX) {
                        const onPositionChangedAction = this.measurer.onPositionChanged;
                        this.measurer.onPositionChanged = undefined;
                        this.measurer.moveLine(newLeftLineX, true);
                        this.measurer.onPositionChanged = onPositionChangedAction;
                      }
                      if (this.measurer.rightLineX != newRightLineX) {
                        const onPositionChangedAction = this.measurer.onPositionChanged;
                        this.measurer.onPositionChanged = undefined;
                        this.measurer.moveLine(newRightLineX, false);
                        this.measurer.onPositionChanged = onPositionChangedAction;
                      }
                    }
                    isNeedUpdate = true;
                  }
                }
              }
              break;
          }
        }
      }
    }

    if (isNeedUpdate) {
      this._updateMeanValuesByMeasurer();
    }
  }

  _forSelectedChannel(bufferIndicesMap, action) {
    const selectedDataSourceId = this.plotObject.selectedChannel.id_dataSource;
    const selectedChannelId = this.plotObject.selectedChannel.id_channel;
    const channelSettings = this.plotObject.getChannelSettings(selectedDataSourceId, selectedChannelId);

    const channelsMap = bufferIndicesMap.get(selectedDataSourceId);
    const configurationsMap = channelsMap.get(selectedChannelId);
    const signalsMap = configurationsMap.get(channelSettings.currConfiguration);
    const configurationSettings = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    configurationSettings.signals.forEach(signalSettings => {
      action(channelSettings, signalSettings, signalsMap);
    });
  }

  _forAllChannels(bufferIndicesMap, action) {
    const dataSources = this.plotObject.settings.dataSources;
    for (let sourceIndex = 0; sourceIndex < dataSources.length; sourceIndex++) {
      const dataSourceSettings = dataSources[sourceIndex];
      const channelsMap = bufferIndicesMap.get(dataSourceSettings.id);
      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const channelSettings = dataSourceSettings.channels[channelIndex];
        const configurationsMap = channelsMap.get(channelSettings.id);
        const signalsMap = configurationsMap.get(channelSettings.currConfiguration);
        const configurationSettings = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
        for (let signalIndex = 0; signalIndex < configurationSettings.signals.length; signalIndex++) {
          const signalSettings = configurationSettings.signals[signalIndex];
          action(channelSettings, signalSettings, signalsMap);
        }
      }
    }
  }

  _updateMaxX() {
    let newMaxX = undefined;
    this._forSelectedChannel(this.cursorBufferIndices, (channelSettings, signalSettings, signalsMap) => {
      let signalObject = this.plotObject.getSignalObject(channelSettings.id_dataSource, channelSettings.id, signalSettings.id);
      const cursorLineInfo = signalsMap.get(signalSettings.id);
      if (cursorLineInfo === undefined) {
        return;
      }
      const maxX = signalObject.getLastValidPointX();
      if (newMaxX === undefined || newMaxX > maxX) {
        newMaxX = maxX;
      }
    });
    if (newMaxX !== undefined) {
      this.cursor.settings.maxX = newMaxX;
      this.measurer.settings.maxX = newMaxX;
    }
  }

  updateCursorAndMeasurerPosition(isNeedSyncCursorAndMeasurer = true) {
    if (this.measurerMode == "cursor") {
      this.updateCursorBufferIndicesMap(this.cursor.linePos);
      if (isNeedSyncCursorAndMeasurer) {
        this._syncCursorPositionToMeasurer();
      }
      this._updateMeanValuesByCursor();
    } else if (this.measurerMode == "measurer") {
      this.updateLeftLineMeasurerBufferIndicesMap(this.measurer.leftLineX);
      this.updateRightLineMeasurerBufferIndicesMap(this.measurer.rightLineX);
      if (isNeedSyncCursorAndMeasurer) {
        this._syncMeasurerPositionToCursor();
      }
      this._updateMeanValuesByMeasurer();
    }
    this.plotObject.tryUpdateMeanSignalsValue();
  }

  _updateMeanValuesByCursor() {
    this._forAllChannels(this.cursorBufferIndices, (channelSettings, signalSettings, signalsMap) => {
      const channelLeftArea = this.plotObject.getChannelLeftArea(channelSettings.id_dataSource, channelSettings.id);
      const meanValueLabels = channelLeftArea.findItem("mean-value-labels");
      let signalObject = this.plotObject.getSignalObject(channelSettings.id_dataSource, channelSettings.id, signalSettings.id);
      const signalIndex = signalsMap.get(signalSettings.id);
      if (signalIndex === undefined) {
        return;
      }
      const unitScale = this.plotObject.getChannelUnitScale(channelSettings);
      const ampl = signalObject.getData(signalIndex.index) * unitScale;
      const meanValueLabel = meanValueLabels.findItem(`${signalSettings.id}`);
      if (meanValueLabel !== undefined && ampl !== undefined) {
        const unitText = this.plotObject.getChannelUnitText(channelSettings);
        const text = `${roundNumber(ampl, 2)} ${unitText}`;
        if (text != meanValueLabel.item.settings.title) {
          meanValueLabel.item.setText(text);
          this.onMeanValuesUpdated?.(channelSettings.id_dataSource, channelSettings.id, signalSettings.id, text, "cursor");
        }
      }
    });
  }

  _createLineColorText(text, color) {
    return `<tspan style="fill:${color}">${text}</tspan>`;
  }

  _updateMeanValuesByMeasurer() {
    this.plotObject.settings.dataSources.forEach(dataSourceSettings => {
      const leftLineChannelsMap = this.measurerLeftBufferIndices.get(dataSourceSettings.id);
      const rightLineChannelsMap = this.measurerRightBufferIndices.get(dataSourceSettings.id);
      dataSourceSettings.channels.forEach(channelSettings => {
        const unitScale = this.plotObject.getChannelUnitScale(channelSettings);
        const leftLineConfigurationsMap = leftLineChannelsMap.get(channelSettings.id);
        const leftLineSignalsMap = leftLineConfigurationsMap.get(channelSettings.currConfiguration);
        const rightLineConfigurationsMap = rightLineChannelsMap.get(channelSettings.id);
        const rightLineSignalsMap = rightLineConfigurationsMap.get(channelSettings.currConfiguration);

        const channelLeftArea = this.plotObject.getChannelLeftArea(dataSourceSettings.id, channelSettings.id);
        const meanValueLabels = channelLeftArea.findItem("mean-value-labels");
        const currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
        currConfiguration.signals.forEach(signalSettings => {
          let signalObject = this.plotObject.getSignalObject(dataSourceSettings.id, channelSettings.id, signalSettings.id);
          const leftLineInfo = leftLineSignalsMap.get(signalSettings.id);
          const rightLineInfo = rightLineSignalsMap.get(signalSettings.id);
          if (leftLineInfo === undefined || rightLineInfo === undefined) {
            return;
          }
          const leftAmpl = signalObject.getData(leftLineInfo.index) * unitScale;
          const rightAmpl = signalObject.getData(rightLineInfo.index) * unitScale;
          const minMax = signalObject.getMinMax(leftLineInfo.index, rightLineInfo.index);
          const meanValueLabel = meanValueLabels.findItem(`${signalSettings.id}`);
          if (meanValueLabel !== undefined && leftAmpl !== undefined && rightAmpl !== undefined && minMax !== undefined) {
            const diffAmpl = rightAmpl - leftAmpl;
            const diffMinMax = (minMax.max - minMax.min) * unitScale;
            const leftAmplText = this._createLineColorText(roundNumber(leftAmpl, 2), this.measurer.settings.leftLineColor);
            const rightAmplText = this._createLineColorText(roundNumber(rightAmpl, 2), this.measurer.settings.rightLineColor);
            const unitText = this.plotObject.getChannelUnitText(channelSettings);
            const meanValueLabelText = this._createLineColorText(
              `${leftAmplText},${rightAmplText},${roundNumber(diffAmpl, 2)},${roundNumber(diffMinMax, 2)} ${unitText}`,
              this.measurer.settings.commonLabelColor
            );
            /*
            const meanValueLabelText = this._createLineColorText(
              `${leftAmplText},${rightAmplText},${roundNumber(diffAmpl, 2)},${roundNumber(diffMinMax, 2)} ${channelSettings.unit.text}`,
              this.measurer.settings.commonLabelColor
            );
            */
            meanValueLabel.item.setText(meanValueLabelText);
            this.onMeanValuesUpdated?.(dataSourceSettings.id, channelSettings.id, signalSettings.id, meanValueLabelText, "measurer");
          }
        });
      });
    });
  }

  _setMeasurerModeToMeasurer() {
    this.cursor.setVisibility(false);
    this.measurer.setVisibility(true);
    const leftPanelWifth = this.plotObject.itemsSettings.measurer.leftPanelWidth.calcValue();
    this._changeLeftPanelWidth(leftPanelWifth);
    this.measurer.updateHtmlParentOffset();
    this._updateMaxX();
    this._updateMeanValuesByMeasurer();
    this.plotObject.settings.canvas.refreshGraphics();
  }

  _setMeasurerModeToCursor() {
    this.measurer.setVisibility(false);
    this.cursor.setVisibility(true);
    const leftPanelWifth = this.plotObject.itemsSettings.cursor.leftPanelWidth.calcValue();
    this._changeLeftPanelWidth(leftPanelWifth);
    this.cursor.updateHtmlParentOffset();
    this._updateMaxX();
    this._updateMeanValuesByCursor();
    this.plotObject.settings.canvas.refreshGraphics();
  }

  _changeLeftPanelWidth(width) {
    this.plotObject.signalsBox.settings.contentBorders.leftCentralWidth = new FixedDimension(width);
    this.plotObject.signalsBox.settings.contentBorders.leftWidth = new FixedDimension(width);
    const meanderX = width - this.plotObject.itemsSettings.meander.location.x - this.plotObject.itemsSettings.meander.size.width;
    this.plotObject.meander.setLocation(new FixedDimension(meanderX), new FixedDimension(2));
    this.plotObject.meanderLabelPanel.settings.padding.right = new FixedDimension(width > 100 ? 20 : 10);
    this.plotObject.createBorders();
  }
}
